import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQueries, useQueryClient } from '@tanstack/react-query';
import { ChevronRight, ClipboardList } from 'lucide-react';
import { getApiErrorMessage } from '../services/api';
import { forgetOrder, rememberOrder, rupees, shopApi, useSavedOrders } from './shopApi';

const STATUS_TONE: Record<string, string> = {
  Delivered: 'bg-success-50 text-success-700',
  Cancelled: 'bg-critical-50 text-critical-700',
  Confirmed: 'bg-info-50 text-info-700',
  'Out for delivery': 'bg-info-50 text-info-700',
};

/** Order number + mobile number -> the order, then it is saved on this phone. */
export function LookupForm({ initialRef = '' }: { initialRef?: string }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [ref, setRef] = useState(initialRef);
  const [mobile, setMobile] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const find = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const o = await shopApi.lookup(ref.trim(), mobile);
      rememberOrder(o);
      qc.setQueryData(['shop-order', o.ref], o);
      navigate(`/shop/order/${o.ref}`);
    } catch (err) {
      setError(getApiErrorMessage(err, 'No order found.'));
      setBusy(false);
    }
  };
  const cls = 'w-full rounded-lg border border-ink-300/50 bg-white px-3 py-2.5 text-base focus:border-brand-green-600 focus:outline-none';
  return (
    <form onSubmit={find} className="flex flex-col gap-2.5 rounded-xl bg-white p-4">
      <p className="text-sm font-semibold">Find an order</p>
      <input className={`${cls} uppercase tracking-wide`} placeholder="Order number, e.g. SK-7KQ3-M9XD" value={ref} onChange={(e) => setRef(e.target.value)} />
      <input className={cls} inputMode="numeric" placeholder="Mobile number used for the order" value={mobile} onChange={(e) => setMobile(e.target.value)} />
      {error && <p className="text-xs font-medium text-critical-600">{error}</p>}
      <button type="submit" disabled={busy || ref.trim().length < 8 || mobile.replace(/\D/g, '').length < 10} className="rounded-lg bg-brand-green-700 py-2.5 font-semibold text-white disabled:bg-ink-300">
        {busy ? 'Finding…' : 'Find my order'}
      </button>
    </form>
  );
}

export default function OrdersPage() {
  const saved = useSavedOrders();
  const results = useQueries({
    queries: saved.map((s) => ({ queryKey: ['shop-order', s.ref], queryFn: () => shopApi.get(s.ref, s.token), retry: 0, staleTime: 30_000 })),
  });
  return (
    <div className="flex flex-col gap-3">
      <h1 className="text-xl font-bold text-brand-green-900">My orders</h1>
      {saved.length === 0 ? (
        <div className="flex flex-col items-center gap-2 rounded-xl bg-white p-8 text-center text-sm text-ink-500">
          <ClipboardList className="h-8 w-8 text-brand-green-600" />
          Orders you place on this phone show here.
          <Link to="/shop" className="mt-1 rounded-full bg-brand-green-700 px-5 py-2 font-semibold text-white">Start shopping</Link>
        </div>
      ) : (
        <ul className="flex flex-col gap-2">
          {saved.map((s, i) => {
            const o = results[i]?.data;
            const lost = results[i]?.isError;
            return (
              <li key={s.ref} className="rounded-xl bg-white">
                {lost ? (
                  <div className="flex items-center justify-between p-3 text-sm">
                    <span><b>{s.ref}</b> <span className="text-ink-400">— opened on another phone</span></span>
                    <button type="button" className="text-xs text-ink-400 underline" onClick={() => forgetOrder(s.ref)}>Remove</button>
                  </div>
                ) : (
                  <Link to={`/shop/order/${s.ref}`} className="flex items-center gap-3 p-3">
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold tracking-wide">{s.ref}</p>
                      <p className="text-xs text-ink-500">{new Date(s.placed).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })} · {rupees(o?.total ?? s.total)}{o ? ` · ${o.delivery_slot} ${o.delivery_date}` : ''}</p>
                    </div>
                    {o && (
                      <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${STATUS_TONE[o.status] ?? (o.payment_status === 'Paid - to verify' ? 'bg-warning-50 text-warning-700' : 'bg-brand-gold-50 text-brand-gold-800')}`}>
                        {o.status === 'Placed' ? (o.payment_status === 'Awaiting payment' ? 'Pay now' : o.payment_status === 'Not received' ? 'Payment not found' : o.payment_status === 'Cash on delivery' ? 'Placed' : 'Checking payment') : o.status}
                      </span>
                    )}
                    <ChevronRight className="h-4 w-4 text-ink-300" />
                  </Link>
                )}
              </li>
            );
          })}
        </ul>
      )}
      <LookupForm />
    </div>
  );
}
